import { useQuery } from '@tanstack/react-query';
import { PkButton, PkDivider, PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useCallback, useMemo, useRef, useState } from 'react';

import type { HeaderCompetition } from '../../model';
import { useHeaderOptions } from '../../model';
import { handleHeaderItemClick, sendHeaderTracking } from '../../utils';
import CompetitionBadge from '../CompetitionBadge';
import FeaturedItem from '../FeaturedItem';
import Overlay from '../Overlay';

import { useLivescore } from '@hooks/useLivescore';
import { useWindowSize } from '@hooks/useWindowSize';
import { getEndpointQuery } from '@queries/common';
import LiveIndicator from '@shared/LiveIndicator';
import { useTranslateFn } from '@shared/Translate';
import { IS_TEST } from '@utilities/browser';

import * as styles from './styles.module.scss';

const handleLiveScoresClick = handleHeaderItemClick({ trackText: 'Live scores' });

const Header = () => {
  const { isDesktop } = useWindowSize();
  const { options } = useHeaderOptions();
  const t = useTranslateFn();

  const refetchInterval = !IS_TEST && options.liveScoreInterval > 0 ? options.liveScoreInterval * 1000 : false;
  const { data: liveCompetitions } = useQuery({
    ...getEndpointQuery<HeaderCompetition[]>('livescores'),
    placeholderData: [],
    refetchInterval,
  });

  const overlayTriggerRef = useRef<HTMLPkButtonElement>(null);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const liveMatches = useLivescore(options.liveScoreCompetitions);

  const featuredItems = useMemo(() => options.menu.find((m) => m.id === 'featured')?.menuItems, [options.menu]);

  const getCompMatches = useCallback(
    (matchIds: string[]) => liveMatches.filter((m) => matchIds.includes(m.id)),
    [liveMatches],
  );

  const toggleOverlay = (open: boolean) => {
    setOverlayOpen(open);
    sendHeaderTracking('all sections panel', open ? 'Open' : 'Close');
  };

  const handleOverlayTriggerClick = () => {
    toggleOverlay(!overlayOpen);
  };

  const handleOverlayClose = () => {
    toggleOverlay(false);
    overlayTriggerRef.current?.focus();
  };

  return (
    <div
      className={clsx(
        'pk-d--flex pk-justify-content--between pk-align-items--center pk-gap--l pk-w--100',
        styles.container,
      )}
    >
      <div className='pk-d--flex pk-justify-content--between pk-align-items--center pk-gap--s pk-w--100'>
        <div
          className={clsx(
            styles.competitions,
            'pk-d--flex pk-align-items--center pk-flex--wrap pk-w--100 pk-pr-sm--l pk-gap--xs pk-gap-sm--l pk-gap-md--xl3 pk-gap-lg--xl4',
          )}
        >
          {isDesktop && !!liveMatches.length && (
            <a
              href='/match-calendar/'
              target='_blank'
              aria-label={t('livescores')}
              onClick={handleLiveScoresClick}
              onAuxClick={handleLiveScoresClick}
              className='pk-d--flex pk-align-items--center'
            >
              <LiveIndicator className={styles.liveIndicator} variant='stroke' label='livescores' size='M' />
            </a>
          )}
          {liveCompetitions?.map((c) => (
            <CompetitionBadge key={`${c.id}-${c.phase}`} competition={c} liveMatches={getCompMatches(c.matchIds)} />
          ))}
          <div>&nbsp;</div>
        </div>
        <div className={clsx('pk-d--flex pk-justify-content--end pk-align-items--center', styles.button)}>
          <PkButton
            ref={overlayTriggerRef}
            highContrast
            appearance='text'
            onPkClick={handleOverlayTriggerClick}
            aria-expanded={overlayOpen}
          >
            <span className='pk-text--nowrap pk-font-size--s pk-font-size-sm--m'>
              {t(overlayOpen ? 'see_less' : 'all-competitions')}
            </span>
            <PkIcon
              className='pk-ml--xs2'
              name={overlayOpen ? 'navigation-chevron-up' : 'navigation-chevron-down'}
              color='interaction-high-contrast'
            />
          </PkButton>
          <Overlay open={overlayOpen} onClose={handleOverlayClose} />
        </div>
      </div>
      {isDesktop && (
        <>
          <PkDivider className={clsx(styles.divider, 'pk-m--0')} direction='vertical' />
          {!!featuredItems?.length && featuredItems.map((m) => <FeaturedItem key={m.id} item={m} />)}
        </>
      )}
    </div>
  );
};

export default memo(Header);
